import { el } from './dom.js';
import { graphicsPrefs, type ChaseView, type Quality, type ViewDistance } from '../graphicsPrefs.js';
import { t } from '../i18n/i18n.js';

/**
 * The graphics settings, as a block inside the Escape menu: the quality and where the chase
 * camera sits. Both take effect at once, mid-race, and are remembered.
 *
 * It borrows the sound panel's segmented buttons (`audioSettings.css`) rather than styling a
 * second set: the two blocks sit one above the other in the same menu, and two different
 * kinds of toggle side by side would read as two different kinds of thing.
 */
export class GraphicsSettings {
  readonly root: HTMLElement;
  private readonly qualityButtons: Record<Quality, HTMLButtonElement>;
  private readonly viewButtons: Record<ChaseView, HTMLButtonElement>;
  private readonly distanceButtons: Record<ViewDistance, HTMLButtonElement>;
  private readonly hint: HTMLElement;

  constructor() {
    const choice = (label: string, id: string, pick: () => void) => {
      const button = el('button', { class: 'audio-settings-source', type: 'button', id }, label);
      button.addEventListener('click', () => {
        pick();
        this.sync();
      });
      return button;
    };
    this.qualityButtons = {
      high: choice(t('High'), 'quality-high', () => graphicsPrefs.setQuality('high')),
      low: choice(t('Low'), 'quality-low', () => graphicsPrefs.setQuality('low')),
    };
    this.viewButtons = {
      standard: choice(t('Above'), 'chase-standard', () => graphicsPrefs.setChaseView('standard')),
      low: choice(t('Low and close'), 'chase-low', () => graphicsPrefs.setChaseView('low')),
    };
    this.distanceButtons = {
      near: choice(t('Near'), 'distance-near', () => graphicsPrefs.setViewDistance('near')),
      medium: choice(t('Medium'), 'distance-medium', () => graphicsPrefs.setViewDistance('medium')),
      far: choice(t('Everything'), 'distance-far', () => graphicsPrefs.setViewDistance('far')),
    };
    this.hint = el('p', { class: 'audio-settings-hint' }, '');

    // Its own root class, sharing the sound panel's rule: two `.audio-settings-panel`s in one
    // menu is two of a thing that is meant to be one, as far as anything that looks it up goes.
    this.root = el(
      'div',
      { class: 'graphics-settings' },
      el('div', { class: 'audio-settings-label' }, t('Quality')),
      el('div', { class: 'audio-settings-source-row' }, this.qualityButtons.high, this.qualityButtons.low),
      this.hint,
      el('div', { class: 'audio-settings-divider' }),
      el('div', { class: 'audio-settings-label' }, t('Chase camera')),
      el('div', { class: 'audio-settings-source-row' }, this.viewButtons.standard, this.viewButtons.low),
      el('div', { class: 'audio-settings-divider' }),
      el('div', { class: 'audio-settings-label' }, t('Draw distance')),
      el(
        'div',
        { class: 'audio-settings-source-row' },
        this.distanceButtons.near,
        this.distanceButtons.medium,
        this.distanceButtons.far,
      ),
      el(
        'p',
        { class: 'audio-settings-hint' },
        t('A big region is smoother drawn nearer: the city fades into the haze instead. The view from above always sees all of it.'),
      ),
    );
    this.sync();
  }

  private sync(): void {
    for (const [quality, button] of Object.entries(this.qualityButtons)) {
      button.classList.toggle('is-active', graphicsPrefs.quality === quality);
    }
    for (const [distance, button] of Object.entries(this.distanceButtons)) {
      button.classList.toggle('is-active', graphicsPrefs.viewDistance === distance);
    }
    for (const [view, button] of Object.entries(this.viewButtons)) {
      button.classList.toggle('is-active', graphicsPrefs.chaseView === view);
    }
    this.hint.textContent =
      graphicsPrefs.quality === 'high'
        ? t('Every frame is graded and the sun glares. Drop to Low if the race stutters.')
        : t('Fewer pixels, no reflections and no glow. For phones and older laptops.');
  }
}
